import {
  useMutation,
  UseMutationResult,
  UseMutationOptions as UseMutationOptionsType,
} from '@tanstack/react-query';
import { PageTemplateAPI } from 'api/page-template';
import {
  CreatePageTemplatePayload,
  PageTemplate,
  PageType,
  SourceType,
  UpdatePageTemplatePayload,
} from 'api/page-template/types';

type UseMutationOptions<TData, TVariables> = Omit<
  UseMutationOptionsType<TData, Error, TVariables>,
  'mutationFn'
>;

type CreatePageTemplateMutationOptions<
  PageTypeT extends PageType = PageType,
  SourceTypeT extends SourceType = SourceType,
> = UseMutationOptions<
  PageTemplate<PageTypeT, SourceTypeT>,
  CreatePageTemplatePayload<PageTypeT, SourceTypeT>
>;

const useCreatePageTemplateMutation = <
  PageTypeT extends PageType = PageType,
  SourceTypeT extends SourceType = SourceType,
>(
  options?: CreatePageTemplateMutationOptions<PageTypeT, SourceTypeT>,
  pageType?: PageType,
): UseMutationResult<
  PageTemplate<PageTypeT, SourceTypeT>,
  Error,
  CreatePageTemplatePayload<PageTypeT, SourceTypeT>
> => {
  /* c8 ignore start - React Query mutations tested via integration tests */
  return useMutation<
    PageTemplate<PageTypeT, SourceTypeT>,
    Error,
    CreatePageTemplatePayload<PageTypeT, SourceTypeT>
  >({
    mutationFn: (payload: CreatePageTemplatePayload<PageTypeT, SourceTypeT>) =>
      PageTemplateAPI.createPageTemplate(payload, pageType),
    ...options,
  });
  /* c8 ignore stop */
};

type UpdatePageTemplateMutationOptions<
  PageTypeT extends PageType = PageType,
  SourceTypeT extends SourceType = SourceType,
> = UseMutationOptions<
  PageTemplate<PageTypeT, SourceTypeT>,
  UpdatePageTemplatePayload<PageTypeT, SourceTypeT>
>;

const useUpdatePageTemplateMutation = <
  PageTypeT extends PageType = PageType,
  SourceTypeT extends SourceType = SourceType,
>(
  options?: UpdatePageTemplateMutationOptions<PageTypeT, SourceTypeT>,
  pageType?: PageType,
): UseMutationResult<
  PageTemplate<PageTypeT, SourceTypeT>,
  Error,
  UpdatePageTemplatePayload<PageTypeT, SourceTypeT>
> => {
  /* c8 ignore start - React Query mutations tested via integration tests */
  return useMutation<
    PageTemplate<PageTypeT, SourceTypeT>,
    Error,
    UpdatePageTemplatePayload<PageTypeT, SourceTypeT>
  >({
    mutationFn: (payload: UpdatePageTemplatePayload<PageTypeT, SourceTypeT>) =>
      PageTemplateAPI.updatePageTemplate<PageTypeT, SourceTypeT>(
        payload,
        pageType,
      ),
    ...options,
  });
  /* c8 ignore stop */
};

type UseDeletePageTemplateMutationOptions = UseMutationOptions<void, number>;

// pageType is optional so existing callers keep hitting the legacy route; passing it opts the
// request into the permission checked /pages/{group}/template route.
/* c8 ignore start - React Query mutations tested via integration tests */
const useDeletePageTemplateMutation = (
  queryOptions?: UseDeletePageTemplateMutationOptions,
  pageType?: PageType,
): UseMutationResult<void, Error, number> =>
  useMutation({
    mutationFn: (templateId: number) =>
      PageTemplateAPI.deletePageTemplate(templateId, pageType),
    ...queryOptions,
  });
/* c8 ignore stop */

type UseUpdatePageTemplateOrderMutationOptions = UseMutationOptions<
  void,
  { templateId: number; order: number }
>;

/* c8 ignore start - React Query mutations tested via integration tests */
const useUpdatePageTemplateOrderMutation = (
  queryOptions?: UseUpdatePageTemplateOrderMutationOptions,
  pageType?: PageType,
): UseMutationResult<void, Error, { templateId: number; order: number }> =>
  useMutation({
    mutationFn: ({ templateId, order }) =>
      PageTemplateAPI.updatePageTemplateOrder(templateId, order, pageType),
    ...queryOptions,
  });
/* c8 ignore stop */

type UseUpdatePageTemplateStatusMutationOptions = UseMutationOptions<
  void,
  { templateId: number; status: boolean }
>;

/* c8 ignore start - React Query mutations tested via integration tests */
const useUpdatePageTemplateStatusMutation = (
  queryOptions?: UseUpdatePageTemplateStatusMutationOptions,
  pageType?: PageType,
): UseMutationResult<void, Error, { templateId: number; status: boolean }> =>
  useMutation({
    mutationFn: ({ templateId, status }) =>
      PageTemplateAPI.updatePageTemplateStatus(templateId, status, pageType),
    ...queryOptions,
  });
/* c8 ignore stop */

export {
  type UseDeletePageTemplateMutationOptions,
  type UseUpdatePageTemplateOrderMutationOptions,
  type UseUpdatePageTemplateStatusMutationOptions,
  type CreatePageTemplateMutationOptions,
  type UpdatePageTemplateMutationOptions,
  useDeletePageTemplateMutation,
  useUpdatePageTemplateOrderMutation,
  useUpdatePageTemplateStatusMutation,
  useCreatePageTemplateMutation,
  useUpdatePageTemplateMutation,
};
